import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart, ReferenceLine,
} from 'recharts';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  PiggyBank,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  Sliders,
  RotateCcw,
} from 'lucide-react';

interface BudgetVsActual {
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  variancePct: number;
}

interface Budget {
  id: string;
  categoryId: string;
  categoryName: string;
  businessProfileId: string | null;
  businessProfileName: string | null;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  budgetAmount: number;
  notes: string | null;
}

interface ExpenseCategory {
  id: string;
  name: string;
  slug: string;
}

interface BusinessProfile {
  id: string;
  name: string;
}

function getPeriodDates(periodType: string, year: number, month: number): { start: string; end: string } {
  switch (periodType) {
    case 'monthly': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    case 'quarterly': {
      const qStart = Math.floor(month / 3) * 3;
      const start = new Date(year, qStart, 1);
      const end = new Date(year, qStart + 3, 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    case 'annual': {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }
    case 'weekly': {
      const now = new Date(year, month, 1);
      const dayOfWeek = now.getDay();
      const start = new Date(now);
      start.setDate(start.getDate() - dayOfWeek);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    default:
      return { start: '', end: '' };
  }
}

// Prior comparable period (immediately preceding window of the same length).
// Used for the period-over-period actual delta on the Budget vs Actual summary.
function getPriorPeriodDates(periodType: string, year: number, month: number): { start: string; end: string } {
  switch (periodType) {
    case 'monthly':
      return getPeriodDates('monthly', month === 0 ? year - 1 : year, month === 0 ? 11 : month - 1);
    case 'quarterly': {
      const qStart = Math.floor(month / 3) * 3;
      const priorQStart = qStart === 0 ? 9 : qStart - 3;
      const priorYear = qStart === 0 ? year - 1 : year;
      return getPeriodDates('quarterly', priorYear, priorQStart);
    }
    case 'annual':
      return getPeriodDates('annual', year - 1, 0);
    case 'weekly': {
      // Step back one week from the current period's start
      const cur = getPeriodDates('weekly', year, month);
      const start = new Date(cur.start);
      start.setDate(start.getDate() - 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    default:
      return { start: '', end: '' };
  }
}

const periodLabel: Record<string, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

export function BudgetsPage() {
  const queryClient = useQueryClient();
  const now = new Date();
  const [periodType, setPeriodType] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);

  const { start: periodStart, end: periodEnd } = getPeriodDates(periodType, selectedYear, selectedMonth);
  const { start: priorPeriodStart, end: priorPeriodEnd } = getPriorPeriodDates(periodType, selectedYear, selectedMonth);

  const { data: categoriesData } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => api.getExpenseCategories(),
  });

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: bvaData, isLoading: bvaLoading } = useQuery({
    queryKey: ['budgetVsActual', { periodType, periodStart, periodEnd, businessProfileId: profileFilter }],
    queryFn: () => api.getBudgetVsActual({
      periodType,
      periodStart,
      periodEnd,
      businessProfileId: profileFilter || undefined,
    }),
    enabled: !!periodStart && !!periodEnd,
  });

  // Prior comparable period — used for period-over-period actual delta on the totals row.
  const { data: priorBvaData } = useQuery({
    queryKey: ['budgetVsActualPrior', { periodType, priorPeriodStart, priorPeriodEnd, businessProfileId: profileFilter }],
    queryFn: () => api.getBudgetVsActual({
      periodType,
      periodStart: priorPeriodStart,
      periodEnd: priorPeriodEnd,
      businessProfileId: profileFilter || undefined,
    }),
    enabled: !!priorPeriodStart && !!priorPeriodEnd,
  });

  const { data: budgetsData, isLoading: budgetsLoading } = useQuery({
    queryKey: ['budgets', { page, periodType, businessProfileId: profileFilter }],
    queryFn: () => api.getBudgets({
      page,
      limit: 20,
      periodType: periodType || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const categories = (categoriesData?.data || []) as ExpenseCategory[];
  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const bvaItems = (bvaData?.data || []) as BudgetVsActual[];
  const budgets = (budgetsData?.data || []) as Budget[];
  const pagination = budgetsData?.pagination;

  const totalBudget = bvaItems.reduce((sum, b) => sum + b.budgetAmount, 0);
  const totalActual = bvaItems.reduce((sum, b) => sum + b.actualAmount, 0);
  const totalVariance = totalBudget - totalActual;

  // Period-over-period actual delta for the totals row.
  const priorBvaItems = (priorBvaData?.data || []) as BudgetVsActual[];
  const priorTotalActual = priorBvaItems.reduce((sum, b) => sum + b.actualAmount, 0);
  const actualDelta = totalActual - priorTotalActual;
  const actualDeltaPct = priorTotalActual > 0 ? (actualDelta / priorTotalActual) * 100 : null;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createBudget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budgetVsActual'] });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateBudget(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budgetVsActual'] });
      setDialogOpen(false);
      setEditingBudget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBudget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budgetVsActual'] });
    },
  });

  function openCreate() {
    setEditingBudget(null);
    setDialogOpen(true);
  }

  function openEdit(budget: Budget) {
    setEditingBudget(budget);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      categoryId: formData.get('categoryId'),
      businessProfileId: formData.get('businessProfileId') || null,
      periodType: formData.get('periodType') || 'monthly',
      periodStart: formData.get('periodStart'),
      periodEnd: formData.get('periodEnd'),
      budgetAmount: parseFloat(formData.get('budgetAmount') as string),
      notes: formData.get('notes') || null,
    };

    if (editingBudget) {
      updateMutation.mutate({ id: editingBudget.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Budgets & Forecasting</h1>
          <p className="text-muted-foreground">Set budget targets and compare against actual spending</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Budget
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={periodType} onValueChange={(v) => { setPeriodType(v); setPage(1); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
          </SelectContent>
        </Select>
        {periodType !== 'annual' && (
          <Select value={String(selectedMonth)} onValueChange={(v) => { setSelectedMonth(parseInt(v)); setPage(1); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={String(selectedYear)} onValueChange={(v) => { setSelectedYear(parseInt(v)); setPage(1); }}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All profiles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Budget vs Actual Comparison */}
      <div className="space-y-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-lg font-semibold">Budget vs. Actual — {periodLabel[periodType]} ({periodStart} to {periodEnd})</h3>
          {actualDeltaPct !== null && (
            <span className="text-xs text-muted-foreground">
              Spend{' '}
              <span className={actualDelta <= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {actualDelta >= 0 ? '+' : ''}{actualDeltaPct.toFixed(1)}%
              </span>{' '}
              vs prior {periodLabel[periodType]?.toLowerCase() || 'period'} ({formatCurrency(priorTotalActual)})
            </span>
          )}
        </div>
        {bvaLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : bvaItems.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ArrowUpDown className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No budget or expense data for this period</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 text-sm font-medium">Category</th>
                    <th className="text-right p-3 text-sm font-medium">Budget</th>
                    <th className="text-right p-3 text-sm font-medium">Actual</th>
                    <th className="text-right p-3 text-sm font-medium">Variance</th>
                    <th className="text-right p-3 text-sm font-medium">%</th>
                  </tr>
                </thead>
                <tbody>
                  {bvaItems.map((item) => (
                    <tr key={item.categoryId} className="border-b hover:bg-muted/30">
                      <td className="p-3 text-sm font-medium">{item.categoryName}</td>
                      <td className="p-3 text-sm text-right">{formatCurrency(item.budgetAmount)}</td>
                      <td className="p-3 text-sm text-right">{formatCurrency(item.actualAmount)}</td>
                      <td className={`p-3 text-sm text-right font-medium ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="inline-flex items-center gap-1">
                          {item.variance >= 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                          {formatCurrency(Math.abs(item.variance))}
                        </span>
                      </td>
                      <td className={`p-3 text-sm text-right ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.variancePct.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/50 font-semibold">
                    <td className="p-3 text-sm">Total</td>
                    <td className="p-3 text-sm text-right">{formatCurrency(totalBudget)}</td>
                    <td className="p-3 text-sm text-right">{formatCurrency(totalActual)}</td>
                    <td className={`p-3 text-sm text-right ${totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(totalVariance))}
                    </td>
                    <td className={`p-3 text-sm text-right ${totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalBudget > 0 ? ((totalVariance / totalBudget) * 100).toFixed(1) : '0.0'}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Budget Targets List */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Budget Targets</h3>
        {budgetsLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : budgets.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <PiggyBank className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No budget targets set</p>
              <Button variant="outline" className="mt-4" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create a budget
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 text-sm font-medium">Category</th>
                    <th className="text-left p-3 text-sm font-medium">Period</th>
                    <th className="text-left p-3 text-sm font-medium">Business Profile</th>
                    <th className="text-right p-3 text-sm font-medium">Budget Amount</th>
                    <th className="text-right p-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map((b) => (
                    <tr key={b.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 text-sm font-medium">{b.categoryName}</td>
                      <td className="p-3 text-sm">
                        {periodLabel[b.periodType] || b.periodType}: {b.periodStart?.split('T')[0]} — {b.periodEnd?.split('T')[0]}
                      </td>
                      <td className="p-3 text-sm">{b.businessProfileName || 'All'}</td>
                      <td className="p-3 text-sm text-right font-medium">{formatCurrency(b.budgetAmount)}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)}>Edit</Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => { if (confirm('Delete this budget?')) deleteMutation.mutate(b.id); }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} budget{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Budget Forecast & Scenario Section */}
      <BudgetForecastSection profileFilter={profileFilter} bvaItems={bvaItems} />

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBudget ? 'Edit Budget' : 'New Budget'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="categoryId">Category *</Label>
              <select
                name="categoryId"
                id="categoryId"
                required
                defaultValue={editingBudget?.categoryId || ''}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="businessProfileId">Business Profile</Label>
              <select
                name="businessProfileId"
                id="businessProfileId"
                defaultValue={editingBudget?.businessProfileId || ''}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All profiles</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="periodType">Period Type</Label>
                <select
                  name="periodType"
                  id="periodType"
                  defaultValue={editingBudget?.periodType || 'monthly'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <Label htmlFor="budgetAmount">Budget Amount *</Label>
                <Input
                  type="number"
                  name="budgetAmount"
                  id="budgetAmount"
                  step="0.01"
                  min="0.01"
                  required
                  defaultValue={editingBudget?.budgetAmount || ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="periodStart">Start Date *</Label>
                <Input
                  type="date"
                  name="periodStart"
                  id="periodStart"
                  required
                  defaultValue={editingBudget?.periodStart ? editingBudget.periodStart.split('T')[0] : periodStart}
                />
              </div>
              <div>
                <Label htmlFor="periodEnd">End Date *</Label>
                <Input
                  type="date"
                  name="periodEnd"
                  id="periodEnd"
                  required
                  defaultValue={editingBudget?.periodEnd ? editingBudget.periodEnd.split('T')[0] : periodEnd}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingBudget?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingBudget ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// Budget Forecast & Scenario Section
// ============================================

interface MonthlyTotal {
  month: string;
  monthName: string;
  totalSpend: number;
  totalRevenue: number;
}

interface CategoryAverage {
  categoryId: string;
  categoryName: string;
  avgMonthlySpend: number;
  monthsWithData: number;
}

interface ForecastData {
  monthlyTotals: MonthlyTotal[];
  currentMonthlyBudget: number;
  categoryAverages: CategoryAverage[];
}

interface ScenarioAdjustment {
  categoryId: string;
  categoryName: string;
  currentAvg: number;
  adjustedAmount: number;
}

function BudgetForecastSection({
  profileFilter,
  bvaItems,
}: {
  profileFilter: string;
  bvaItems: BudgetVsActual[];
}) {
  const [showScenario, setShowScenario] = useState(false);
  const [scenarioAdjustments, setScenarioAdjustments] = useState<ScenarioAdjustment[]>([]);

  const monthsBack = 6;
  const { data: forecastData } = useQuery({
    queryKey: ['budgetForecast', { profileFilter, monthsBack }],
    queryFn: async () => {
      const result = await api.getBudgetForecast({
        monthsBack,
        businessProfileId: profileFilter || undefined,
      });
      return result.data as ForecastData;
    },
  });

  const monthlyTotals = forecastData?.monthlyTotals || [];
  const categoryAverages = forecastData?.categoryAverages || [];
  const currentBudget = forecastData?.currentMonthlyBudget || 0;

  // Compute avg monthly spend from the trend data
  const avgMonthlySpend = useMemo(() => {
    if (monthlyTotals.length === 0) return 0;
    const total = monthlyTotals.reduce((s, m) => s + m.totalSpend, 0);
    return total / monthlyTotals.length;
  }, [monthlyTotals]);

  const avgMonthlyRevenue = useMemo(() => {
    if (monthlyTotals.length === 0) return 0;
    const total = monthlyTotals.reduce((s, m) => s + m.totalRevenue, 0);
    return total / monthlyTotals.length;
  }, [monthlyTotals]);

  // Generate forecast months (6 months forward)
  const forecastChartData = useMemo(() => {
    const items: { month: string; actual?: number; projected?: number; revenue?: number; budget?: number }[] = [];

    for (const m of monthlyTotals) {
      items.push({
        month: m.monthName.substring(0, 3),
        actual: m.totalSpend,
        revenue: m.totalRevenue,
      });
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const lastMonth = monthlyTotals.length > 0
      ? new Date(monthlyTotals[monthlyTotals.length - 1].month + '-01')
      : new Date();

    for (let i = 1; i <= 6; i++) {
      const nextMonth = new Date(lastMonth);
      nextMonth.setMonth(nextMonth.getMonth() + i);

      const projectedSpend = showScenario && scenarioAdjustments.length > 0
        ? scenarioAdjustments.reduce((s, a) => s + a.adjustedAmount, 0)
        : avgMonthlySpend;

      items.push({
        month: monthNames[nextMonth.getMonth()],
        projected: Math.round(projectedSpend),
        revenue: avgMonthlyRevenue,
        budget: currentBudget > 0 ? currentBudget : undefined,
      });
    }

    return items;
  }, [monthlyTotals, avgMonthlySpend, avgMonthlyRevenue, currentBudget, showScenario, scenarioAdjustments]);

  // Initialize scenario from category averages
  const initScenario = useCallback(() => {
    setScenarioAdjustments(
      categoryAverages.map(c => ({
        categoryId: c.categoryId,
        categoryName: c.categoryName,
        currentAvg: c.avgMonthlySpend,
        adjustedAmount: c.avgMonthlySpend,
      }))
    );
    setShowScenario(true);
  }, [categoryAverages]);

  const resetScenario = useCallback(() => {
    setScenarioAdjustments(
      categoryAverages.map(c => ({
        categoryId: c.categoryId,
        categoryName: c.categoryName,
        currentAvg: c.avgMonthlySpend,
        adjustedAmount: c.avgMonthlySpend,
      }))
    );
  }, [categoryAverages]);

  const updateAdjustment = useCallback((categoryId: string, value: number) => {
    setScenarioAdjustments(prev =>
      prev.map(a => a.categoryId === categoryId ? { ...a, adjustedAmount: value } : a)
    );
  }, []);

  const scenarioTotal = scenarioAdjustments.reduce((s, a) => s + a.adjustedAmount, 0);
  const scenarioDiff = scenarioTotal - avgMonthlySpend;

  // Budget vs Actual chart data
  const bvaChartData = useMemo(() => {
    return bvaItems
      .filter(item => item.budgetAmount > 0 || item.actualAmount > 0)
      .slice(0, 10)
      .map(item => ({
        name: item.categoryName.length > 12
          ? item.categoryName.substring(0, 12) + '...'
          : item.categoryName,
        budget: item.budgetAmount,
        actual: item.actualAmount,
      }));
  }, [bvaItems]);

  if (monthlyTotals.length === 0 && bvaItems.length === 0) {
    return null; // Nothing to show yet
  }

  return (
    <div className="space-y-6 border-t pt-6">
      <h2 className="text-xl font-bold">Forecast & Scenario Analysis</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Avg Monthly Spend</CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{formatCurrency(avgMonthlySpend)}</div>
            <p className="text-xs text-muted-foreground">Last {monthlyTotals.length} months</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Avg Monthly Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-emerald-600">{formatCurrency(avgMonthlyRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Monthly Budget</CardTitle>
            <PiggyBank className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-blue-600">{formatCurrency(currentBudget)}</div>
            <p className="text-xs text-muted-foreground">
              {currentBudget > 0 && avgMonthlySpend > 0
                ? `${((avgMonthlySpend / currentBudget) * 100).toFixed(0)}% utilized`
                : 'No budget set'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Projected Annual</CardTitle>
            <ArrowUpDown className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-amber-600">{formatCurrency(avgMonthlySpend * 12)}</div>
            <p className="text-xs text-muted-foreground">At current run rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget vs Actual bar chart */}
        {bvaChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Budget vs Actual by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={bvaChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                  <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" name="Actual" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Spend trend + forecast chart */}
        {forecastChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Spend Trend & Forecast</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={forecastChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                  <Bar dataKey="actual" name="Actual Spend" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="projected" name="Projected" fill="#8b5cf6" radius={[4, 4, 0, 0]} opacity={0.6} />
                  <Line dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  {currentBudget > 0 && (
                    <ReferenceLine y={currentBudget} stroke="#3b82f6" strokeDasharray="5 5" label={{ value: 'Budget', fill: '#3b82f6', fontSize: 10 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Scenario Mode */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Budget Scenario Planner</CardTitle>
          <div className="flex gap-2">
            {!showScenario ? (
              <Button size="sm" variant="outline" onClick={initScenario} disabled={categoryAverages.length === 0}>
                <Sliders className="h-4 w-4 mr-1" />
                Start Scenario
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={resetScenario}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Reset
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowScenario(false)}>
                  Close
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!showScenario ? (
            <div className="text-center py-8 text-muted-foreground">
              <Sliders className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Adjust category-level spending to model different budget scenarios</p>
              <p className="text-xs mt-1">See how changes in each category affect your total monthly spend and forecast</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Scenario summary */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <span className="text-sm font-medium">Scenario Monthly Total: </span>
                  <span className="text-lg font-bold">{formatCurrency(scenarioTotal)}</span>
                </div>
                <div className={`text-sm font-medium ${scenarioDiff <= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                  {scenarioDiff <= 0 ? '' : '+'}{formatCurrency(scenarioDiff)} vs current avg
                  <span className="text-xs text-muted-foreground ml-2">
                    ({scenarioDiff <= 0 ? '' : '+'}{formatCurrency(scenarioDiff * 12)}/yr)
                  </span>
                </div>
              </div>

              {/* Category adjustments */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {scenarioAdjustments.map(adj => {
                  const diff = adj.adjustedAmount - adj.currentAvg;
                  const pctChange = adj.currentAvg > 0 ? (diff / adj.currentAvg) * 100 : 0;
                  return (
                    <div key={adj.categoryId} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{adj.categoryName}</span>
                        {diff !== 0 && (
                          <span className={`text-xs font-medium ${diff <= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                            {diff <= 0 ? '' : '+'}{pctChange.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="h-8 text-sm"
                          value={adj.adjustedAmount}
                          onChange={e => updateAdjustment(adj.categoryId, parseFloat(e.target.value) || 0)}
                          min={0}
                          step={50}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Avg: {formatCurrency(adj.currentAvg)}/mo
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
